import * as React from 'react';
import Box from '@mui/material/Box';
import Fluid from 'components/Fluid';
import LongArrowButton from 'components/LongArrowButton';
import Typography from '@mui/material/Typography';
import useWindowDimensions from 'hooks/useWindowDimensions';
import { Autoplay, EffectFade } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/effect-fade';


const MultiCTACard = ({cards, title, description, imageSide, flush, background, backgroundImage, backgroundProps, padding, ...props}) => {

	const aspectRatio = 9/16;
	const height = 219;
	const imageRef = React.useRef(null);
	const sectionRef = React.useRef(null);
	const [leftOffset, setLeftOffset] = React.useState('0px');
	const [clipWidth, setClipWidth] = React.useState('0px');
	const [isOpen, setIsOpen] = React.useState(false);
	const swiperRef = React.useRef(null);
	const [swiperKey, setSwiperKey] = React.useState(0);
	const [gradient, setGradient] = React.useState('270deg, rgba(0, 0, 0, 0.70) 0%, rgba(0, 0, 0, 0.49) 46.15%, rgba(0, 0, 0, 0.00) 71.63%');
	const [mobileHeight, setMobileHeight] = React.useState(height);
	const { isMobile } = useWindowDimensions();

	const updatePosition = React.useCallback((location, side='left') => {
		if (location?.current) {
			if (isMobile) { 
				if (isOpen) {
					setMobileHeight(sectionRef?.current.offsetHeight)
				} else {
					setMobileHeight(height);
				}
			} else {
				if (isOpen) { 
					if (side === 'left') {
						setGradient('270deg, rgba(0, 0, 0, 0.70) 0%, rgba(0, 0, 0, 0.49) 46.15%, rgba(0, 0, 0, 0.00) 71.63%');
					} else {
						setGradient('90deg, rgba(0, 0, 0, 0.70) 0%, rgba(0, 0, 0, 0.49) 46.15%, rgba(0, 0, 0, 0.00) 71.63%');
					}
				} else {
					if (side === 'left') {
						setLeftOffset(`${location?.current?.offsetLeft}px`);
						setClipWidth(`${location?.current?.offsetLeft + (location?.current?.offsetHeight * aspectRatio)}px`);
						setGradient('270deg, rgba(0, 0, 0, 0.70) 0%, rgba(0, 0, 0, 0.49) 46.15%, rgba(0, 0, 0, 0.00) 71.63%');
					} else {
						setLeftOffset(`${location?.current?.offsetLeft + (location?.current?.offsetWidth - (location?.current?.offsetHeight * aspectRatio))}px`);
						setClipWidth(`${(location?.current?.offsetLeft + (location?.current?.offsetWidth - (location?.current?.offsetHeight * aspectRatio))) + (location?.current?.offsetHeight * aspectRatio)}px`);
						setGradient('90deg, rgba(0, 0, 0, 0.70) 0%, rgba(0, 0, 0, 0.49) 46.15%, rgba(0, 0, 0, 0.00) 71.63%');
					}
				}
			}
		}
	}, [aspectRatio, isMobile, isOpen]);

	const mouseEnterLink = (i) => {
		if(swiperRef?.current) {
			swiperRef.current.slideTo(i);
		}
	}

	React.useEffect(() => {
		if (imageRef?.current) {
			updatePosition(imageRef, imageSide);
			if (isMobile) {
				setGradient(''); 
			} else {
				if (imageSide === 'left') {
					setGradient('270deg, rgba(0, 0, 0, 0.70) 0%, rgba(0, 0, 0, 0.49) 46.15%, rgba(0, 0, 0, 0.00) 71.63%');
				} else {
					setGradient('90deg, rgba(0, 0, 0, 0.70) 0%, rgba(0, 0, 0, 0.49) 46.15%, rgba(0, 0, 0, 0.00) 71.63%');
				}
			}
		}
	}, [imageRef, imageSide, isMobile, updatePosition]);

	React.useEffect(() => {
		if (isOpen) {
			setLeftOffset('0px');
			setClipWidth('100%');
			if(isMobile) {
				setMobileHeight(sectionRef?.current.offsetHeight);
				setGradient('180deg, rgba(0, 0, 0, 0.60) 40.72%, rgba(0, 0, 0, 0.00) 85.46%');
			}
		} else {
			updatePosition(imageRef, imageSide);
			setMobileHeight(height);
			if (isMobile) {
				setGradient('180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.00) 0%');
			}
		}
	}, [isOpen, isMobile, sectionRef, imageSide, updatePosition]);

	React.useEffect(() => {
		if (!!sectionRef.current === false)return;
		const observer = new IntersectionObserver(([entry]) => {
			if (!entry.isIntersecting) {
				updatePosition(imageRef, imageSide);
				setIsOpen(false);
				swiperRef.current.slideTo(0);
			} else {
				setLeftOffset('0px');
				setClipWidth('100%');
				setIsOpen(true);
			}
		}, {
			threshold: .75,
		});
		observer.observe(sectionRef.current);
		return ((element, observer) => ( // It's a complicated looking statement, but it's really just returning a closure with the element and observer as arguments so that it can be unobserved before React kills the ref.
			() => {
				observer.unobserve(element);
			}
		))(sectionRef.current, observer);
	}, [sectionRef, swiperRef, isOpen, updatePosition, setIsOpen, setLeftOffset, setClipWidth, imageSide, imageRef]);

	React.useEffect(() => {
		const handleResize = () => {
			if (!isOpen) { updatePosition(imageRef, imageSide); setSwiperKey(s => s + 1) }
		};

		window.addEventListener("resize", handleResize);
		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, [isOpen, imageSide, updatePosition]);

	if(!cards)return null;

	return (
		<Box
			className={[
				'multiCTACard__section', 
				(padding ? 'padding-normal' : ''), 
				(flush ? 'margin-flush' : ''),
				(background||''),
				(isOpen ? 'active' : '')
			].join(' ')}
		>	
			<Box sx={{position: 'relative'}} ref={sectionRef}>
				<Fluid layout={{xs: "12", ms:"6 6"}} alignItems="center" justifyContent="space-between" className={['multiCTACard__content_grid', `multiCTACard__image_${imageSide}`].join(' ')}>
					{imageSide === 'left' && <div ref={imageRef} className="multiCTACard__image_space" />}
					<div className="multiCTACard__text_wrap">
						{title && <Typography className="multiCTACard__title" component="div" variant="h2">{title}</Typography>}
						{description && <Typography className="multiCTACard__sub_title" component="div" variant="large">{description}</Typography>}
						{cards && cards?.length && <div className="multiCTACard__links">
							{cards.map((card, index) => {
								return (
									<LongArrowButton 
										key={`link_${index}`}
										className="multiCTACard__link" 
										href={card.link}
										onMouseEnter={() => mouseEnterLink(index)}
										align="left"
									>{card.linkText}</LongArrowButton>
								)
							})}
						</div>}
					</div>
					{imageSide === 'right' && <div ref={imageRef} className="multiCTACard__image_space" />}
				</Fluid>
				<Swiper
						modules={[Autoplay, EffectFade]}
						slidesPerView={1}
						slidesPerGroup={1}
						className={'multiCTACard__bg_image'}
						effect={'fade'}
						fadeEffect={{
							crossFade:true
						}}
						centeredSlides={false}
						spaceBetween={'0'}
						speed={1000}
						loop={true}
						// autoplay={false}
						autoplay={{
							delay: 2000,
						}}
			      		key={swiperKey}
						onBeforeInit={(swiper) => {
							swiperRef.current = swiper;
						}}
						breakpoints={{
							1024: {
								autoplay: false
							}
						}}
						style={{
							clipPath: `polygon(${leftOffset} 0, ${clipWidth} 0%, ${clipWidth} 100%, ${leftOffset} 100%)`,
							height: (isMobile) ? `${mobileHeight}px` : '100%',
						}}
			      	>
		      			{cards && cards.length > 0 && cards.map((card,index) => {
						return (
							<SwiperSlide 
								key={index} 
								className="multiCTACard__image_slide" 
								style={{
									backgroundImage: `linear-gradient(${gradient}), url(${card?.image}?max-w=1920&format=webp)`,
								}}
							/>
						)
					})}
		      	</Swiper>
		    </Box>
		</Box>
	)
}

export default React.memo(MultiCTACard);